import React, { useEffect, useState } from 'react';
import { Bell, BellOff, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  isPushSupported,
  getPermissionState,
  subscribeToPush,
} from '@/utils/pushNotifications';

const DISMISS_KEY = 'abok_notif_modal_dismissed_at';

/**
 * Post-login blocking modal that requires the user to enable push notifications.
 * Per ABOK policy: no in-app toggles — users can revoke only via OS/browser.
 *
 * Rules:
 *  - If browser doesn't support push: hide silently.
 *  - If permission already granted: silently (re)subscribe and hide.
 *  - If permission denied: show "Enable in browser settings" guidance (dismissible).
 *  - If permission default: show blocking modal with single Enable button.
 */
const EnableNotificationsModal = ({ user }) => {
  const [state, setState] = useState('init'); // init | needs_enable | denied | granted

  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) {
      setState('granted'); // hide silently
      return;
    }
    const perm = getPermissionState();
    if (perm === 'granted') {
      // Silently make sure server has our subscription
      subscribeToPush().catch(() => {});
      setState('granted');
    } else if (perm === 'denied') {
      setState('denied');
    } else {
      setState('needs_enable');
    }
  }, [user]);

  const handleEnable = async () => {
    if (!isPushSupported()) {
      setState('granted');
      return;
    }
    let permission = Notification.permission;
    if (permission === 'default') {
      try {
        permission = await Notification.requestPermission();
      } catch (e) {
        permission = 'denied';
      }
    }
    // Update modal state IMMEDIATELY — don't wait for SW/subscribe (which can hang)
    if (permission === 'granted') {
      setState('granted');
      // Fire-and-forget: subscribe in background; SW issues won't block the UX
      subscribeToPush().catch((err) => console.warn('Push subscribe failed:', err));
    } else if (permission === 'denied') {
      setState('denied');
    }
  };

  const handleDismissDenied = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setState('granted'); // hide for this session
  };

  if (state === 'granted' || state === 'init') return null;

  // For 'denied' state, only show if not recently dismissed (7 days)
  if (state === 'denied') {
    const last = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (last && Date.now() - last < 7 * 24 * 60 * 60 * 1000) return null;
  }

  // 'denied' has no actionable in-app fix (the only resolution is a browser-settings
  // change) — show it as a small dismissible corner banner instead of a full-screen
  // blocking overlay that would intercept clicks on the rest of the app.
  if (state === 'denied') {
    return (
      <div
        className="fixed bottom-4 right-4 z-[9999] w-full max-w-sm bg-white border-2 border-slate-900 shadow-2xl"
        data-testid="enable-notifications-modal"
      >
        <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <h2 className="text-xs uppercase tracking-widest font-semibold">Notifications Blocked</h2>
        </div>
        <div className="px-4 py-4 space-y-3">
          <p className="text-sm text-slate-700">Notifications are currently blocked for this site.</p>
          <div className="bg-slate-100 border-l-4 border-slate-900 p-3 text-xs text-slate-700">
            <p className="font-semibold mb-1">To enable:</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Click the lock icon in your browser's address bar</li>
              <li>Find "Notifications" and switch to "Allow"</li>
              <li>Reload this page</li>
            </ol>
          </div>
          <Button
            variant="outline"
            onClick={handleDismissDenied}
            className="w-full rounded-none border-slate-900 text-slate-900 uppercase tracking-widest text-xs font-semibold"
            data-testid="dismiss-denied-btn"
          >
            <BellOff className="h-4 w-4 mr-2" />
            Continue without notifications
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="enable-notifications-modal"
    >
      <div className="w-full max-w-md bg-white border-2 border-slate-900 shadow-2xl">
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center gap-3">
          <Bell className="h-6 w-6" />
          <h2 className="text-sm uppercase tracking-widest font-semibold">Enable Notifications</h2>
        </div>

        <div className="px-6 py-6 space-y-4">
          <p className="text-slate-700">
            ABOK ERP requires push notifications to alert you about breakdowns,
            SLA breaches, and repair updates in real time.
          </p>
          <p className="text-sm text-slate-500">
            You'll receive only operationally-critical alerts. No marketing,
            no spam.
          </p>
          <Button
            onClick={handleEnable}
            className="w-full rounded-none bg-slate-900 hover:bg-slate-800 text-white uppercase tracking-widest font-semibold py-6"
            data-testid="enable-notifications-btn"
          >
            <Bell className="h-4 w-4 mr-2" />
            Enable Notifications
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EnableNotificationsModal;
